/**
 * Equivalence footnotes let one measured value stand in for another, so the combined result counts
 * only the one that applies. Which value that is depends on the measured data, on the analytic's
 * column and — for DepV footnote 12 — on an asymmetry, so the backend decides it and ships the
 * outcome as {@link IgnoredValues}; deriving it here from priorities and parameters would silently
 * drift away from the actual combined result.
 */
import type { AnalyticalEvaluation, IgnoredValues } from '../types/analyticalEvaluation'

export enum EquivalenceRole {
	/** left out of the combined result, another value counts in its place */
	IGNORED = 'IGNORED',
	/** counts in the combined result in place of one or more ignored values */
	COUNTING = 'COUNTING'
}

export type EquivalenceMark = {
	role: EquivalenceRole
	/** plain names of the values on the other side of the equivalence */
	counterparts: string[]
	/** plain names of the analytics the role applies in, only interesting for a comparison */
	analytics: string[]
}

/**
 * The label a tooltip shows for a value it points at. An equivalence footnote can name a value
 * from the other medium, and the unit is the only thing telling them apart — mg/kg for a Feststoff
 * value, mg/l for an Eluat one — so it travels with the name.
 */
export function getValueLabel(metadata: AnalyticalEvaluation['valueMetadata'], key: string) {
	const entry = metadata?.[key]
	if (!entry) return key

	const unit = entry.unit?.definition ?? ''

	return unit === '' ? entry.name : `${entry.name} [${unit}]`
}
/**
 * The two sets one analytic's entry describes. Never merge them across analytics: in an analytic
 * comparison the same value can be ignored in one column and count in another, so always look it
 * up under the technical name of the column at hand.
 */
function equivalenceSets(ignoredValues: IgnoredValues | undefined, technicalName: string) {
	const forAnalytic = ignoredValues?.[technicalName] ?? {}

	return {
		forAnalytic,
		ignored: new Set(Object.keys(forAnalytic)),
		counting: new Set(Object.values(forAnalytic).flat())
	}
}

/**
 * Merges the per-analytic sets of `technicalNames` — the analytic columns of the page — into
 * the single mark the value's row can show. The sets are disjoint by design; should a key land in
 * both anyway, "does not count" wins.
 *
 * @param labelOf resolves a value key to the label a tooltip shows for it, unit included
 */
export function equivalenceMark(
	ignoredValues: IgnoredValues | undefined,
	technicalNames: string[],
	valueKey: string,
	labelOf: (key: string) => string,
	analyticNameOf: (technicalName: string) => string
): EquivalenceMark | undefined {
	const ignoredIn: string[] = []
	const countingIn: string[] = []
	const ignoredCounterparts = new Set<string>()
	const countingCounterparts = new Set<string>()

	for (const technicalName of technicalNames) {
		const { forAnalytic, ignored, counting } = equivalenceSets(ignoredValues, technicalName)

		if (ignored.has(valueKey)) {
			ignoredIn.push(technicalName)

			for (const coveringKey of forAnalytic[valueKey] ?? []) {
				ignoredCounterparts.add(coveringKey)
			}
		} else if (counting.has(valueKey)) {
			countingIn.push(technicalName)

			// n:m — one covering value can stand in for several ignored ones
			for (const [ignoredKey, coveringKeys] of Object.entries(forAnalytic)) {
				if (coveringKeys.includes(valueKey)) {
					countingCounterparts.add(ignoredKey)
				}
			}
		}
	}

	if (ignoredIn.length) {
		return {
			role: EquivalenceRole.IGNORED,
			counterparts: Array.from(ignoredCounterparts, labelOf),
			analytics: ignoredIn.map(analyticNameOf)
		}
	}

	if (countingIn.length) {
		return {
			role: EquivalenceRole.COUNTING,
			counterparts: Array.from(countingCounterparts, labelOf),
			analytics: countingIn.map(analyticNameOf)
		}
	}

	return undefined
}
